import { RULE_INPUT_GUIDANCE } from "./copy.js";
import { INPUT_CLASS, SECONDARY_ACTION } from "../styles.js";
import { ToggleControl } from "../controls.jsx";
import { GroupHeader, Interaction, Mechanics, OnEndpoints } from "../textRoles.jsx";

// One block for each managed list. `action` opens the shared composer with
// that list as its target.
export const RULE_LISTS = Object.freeze([
    { kind: "deny", label: "Block", action: "Add block rule" },
    { kind: "ask", label: "Require approval", action: "Add approval rule" },
    { kind: "allow", label: "Pre-approve", action: "Add pre-approved rule" },
]);

export const INITIAL_RULE_COMPOSER = Object.freeze({ open: false, kind: "deny", tool: "Bash", pattern: "" });

function ruleGuidanceFor(tool) {
    return RULE_INPUT_GUIDANCE[tool] ?? {
        label: "Specifier (optional)",
        help: `Enter only the value inside ${tool}(...), or leave blank to match every use.`,
        example: "Leave blank to match every use",
    };
}

function RuleComposer({ composer, setComposer, onAdd }) {
    const target = RULE_LISTS.find((list) => list.kind === composer.kind) ?? RULE_LISTS[0];
    const guidance = ruleGuidanceFor(composer.tool);
    const preview = composer.pattern.trim() ? `${composer.tool}(${composer.pattern.trim()})` : composer.tool;
    const add = () => {
        onAdd(composer.kind, preview);
        setComposer({ pattern: "" });
    };
    return (
        <div className="grid gap-[8px] border border-[color:var(--green)] p-[10px]" role="group" aria-label={`Add to ${target.label}`}>
            <div className="flex items-center justify-between gap-[8px]">
                <h4 className="m-0 text-[11px] font-medium text-[color:var(--ink)]">Add to {target.label}</h4>
                <button type="button" className={SECONDARY_ACTION} onClick={() => setComposer({ open: false })}>Close</button>
            </div>
            <div className="grid grid-cols-[120px_minmax(0,1fr)_auto] items-end gap-[6px]">
                <label className="grid gap-[5px]"><span className="text-[10px] font-medium text-[color:var(--ink)]">Tool</span><input className={INPUT_CLASS} value={composer.tool} onChange={(event) => setComposer({ tool: event.target.value, pattern: "" })} placeholder="Bash" /></label>
                <label className="grid gap-[5px]"><span className="text-[10px] font-medium text-[color:var(--ink)]">{guidance.label}</span><input className={INPUT_CLASS} value={composer.pattern} onChange={(event) => setComposer({ pattern: event.target.value })} onKeyDown={(event) => { if (event.key === "Enter") add(); }} placeholder={guidance.example} /></label>
                <button type="button" className={SECONDARY_ACTION} disabled={!composer.tool.trim()} onClick={add}>Add managed rule</button>
            </div>
            <Mechanics>Enter the Claude Code tool name exactly as it appears in a permission rule. {guidance.help} <span className="text-[color:var(--ink)]">Example: <code>{guidance.example}</code></span></Mechanics>
            <Mechanics>Rule to add: <code className="text-[color:var(--ink)]">{preview}</code></Mechanics>
        </div>
    );
}

export function ManagedPermissionRules({ controls, updateControls, canManage, ui, setUi, onOpenCategory }) {
    const composer = ui.ruleComposer;
    const setComposer = (patch) => setUi((value) => ({ ...value, ruleComposer: { ...value.ruleComposer, ...patch } }));
    const addRule = (kind, rule) => updateControls((value) => ({
        ...value,
        rules: {
            ...value.rules,
            [kind]: value.rules[kind].includes(rule) ? value.rules[kind] : [...value.rules[kind], rule],
        },
    }));
    const removeRule = (kind, rule) => updateControls((value) => ({
        ...value,
        rules: { ...value.rules, [kind]: value.rules[kind].filter((item) => item !== rule) },
    }));
    return (
        <>
            <GroupHeader title="Managed permission rules" orientation="The allow, ask, and deny rules a session starts with." keys={["permissions.deny", "permissions.ask", "permissions.allow"]} />
            <div className="grid gap-[8px] md:grid-cols-3">
                {RULE_LISTS.map((list) => (
                    <div key={list.kind} className="grid content-start gap-[8px] border border-[color:var(--line)] p-[10px]" role="group" aria-label={list.label}>
                        <strong className="text-[11px] font-medium text-[color:var(--ink)]">{list.label}</strong>
                        <div className="flex flex-wrap gap-[5px]">{controls.rules[list.kind].map((rule) => <span key={rule} className="inline-flex min-h-[28px] items-center gap-[7px] border border-[color:var(--line)] bg-[color:var(--control-raised-bg)] px-[7px]"><code className="text-[10px]">{rule}</code>{canManage ? <button type="button" aria-label={`Remove ${rule}`} className="border-0 bg-transparent text-[color:var(--muted)]" onClick={() => removeRule(list.kind, rule)}>x</button> : null}</span>)}</div>
                        {canManage ? <button type="button" className="justify-self-start border-0 bg-transparent p-0 text-left text-[10px] text-[color:var(--green)] underline-offset-2 hover:underline" aria-expanded={composer.open && composer.kind === list.kind} onClick={() => setComposer({ open: true, kind: list.kind })}>{list.action}</button> : null}
                    </div>
                ))}
            </div>
            {canManage && composer.open ? <RuleComposer composer={composer} setComposer={setComposer} onAdd={addRule} /> : null}
            <OnEndpoints written={Boolean(controls.rules.deny.length || controls.rules.ask.length || controls.rules.allow.length)}>Block rules are evaluated first and always win. A pre-approved action can resolve outright.</OnEndpoints>
            <Interaction scope="Auto mode">Require approval stays authoritative in Auto, but a pre-approved action can resolve before the classifier ever sees it.</Interaction>
            <button type="button" className={`${SECONDARY_ACTION} justify-self-start`} onClick={() => onOpenCategory("auto")}>Review Auto mode behavior</button>
        </>
    );
}

export function AdvancedPermissions({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Advanced" orientation="Whether user and project permission rules apply alongside managed rules." keys={["allowManagedPermissionRulesOnly"]} />
            <ToggleControl disabled={!canManage} checked={controls.managedRulesOnly} onChange={(checked) => updateControls((value) => ({ ...value, managedRulesOnly: checked }))} label="Only managed permission rules apply" />
            <OnEndpoints written={controls.managedRulesOnly}>{controls.managedRulesOnly ? "User and project permission rules are ignored. The lists above are the complete configurable permission-rule set for a new session." : "The lists above merge with permission rules from user and project settings, and users may define new rules as they work."}</OnEndpoints>
        </>
    );
}
